'use client';

import { ConversationMessages } from '@/components/features/chat/components/messages/ConversationMessages';
import { adviceState, adviceActions } from '@/lib/store/advice.store';
import { observer, useComputed } from '@legendapp/state/react';
import { batch } from '@legendapp/state';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useMemo } from 'react';
import {
  messageVariants,
  contentVariants,
} from '@/lib/helpers/animations/variants';
import { CaseOutcome } from '@/components/features/case/components/CaseOutcome';

const ErrorMessage = ({
  error,
  onRetry,
}: {
  error: Error | null;
  onRetry: () => void;
}) => (
  <motion.div
    variants={messageVariants}
    initial='initial'
    animate='animate'
    exit='exit'
    className='relative w-full rounded-lg border border-red-200 bg-red-50 p-2 px-2 shadow-sm dark:border-red-800/50 dark:bg-red-900/20 dark:shadow-red-900/10 sm:p-4 sm:px-4'
  >
    <div className='flex flex-col space-y-2'>
      <p className='text-sm font-medium text-red-800 dark:text-red-300 sm:text-base'>
        {error?.message || 'An error occurred'}
      </p>
      <button
        onClick={onRetry}
        className='self-start rounded-md bg-red-100 px-2.5 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-200 dark:bg-red-800/30 dark:text-red-300 dark:hover:bg-red-800/50 sm:text-sm'
      >
        Try Again
      </button>
    </div>
  </motion.div>
);

export const ConversationContent = observer(
  ({
    recentPromptRef,
    chatContainerRef,
  }: {
    recentPromptRef: React.RefObject<HTMLDivElement>;
    chatContainerRef: React.RefObject<HTMLDivElement>;
  }) => {
    // Derive conversation data from the store reactively.
    const conversationData = useComputed(() =>
      adviceState.activeConversation.get()
    );
    const isLoading = useComputed(
      () =>
        adviceState.status.get() === 'submitted' ||
        adviceState.status.get() === 'streaming'
    );
    const error = useComputed(() => adviceState.error.get());
    const isFirstAdviceAnimated = useComputed(() =>
      adviceState.isFirstAdviceAnimationComplete.get()
    );

    // Should show case outcome only when the conversation has a caseOutcome
    // AND the first advice animation is complete
    const shouldShowCaseOutcome = useComputed(() => {
      const hasOutcome = !!conversationData.get()?.caseOutcome;
      const isAnimated = isFirstAdviceAnimated.get();
      return hasOutcome && isAnimated;
    });

    // Transform similarCases data into the correct format
    const formattedSimilarCases = useMemo(() => {
      const caseOutcome = conversationData.get()?.caseOutcome;
      if (!caseOutcome?.similarCases) return [];

      // If it's already in the correct format, return as is
      if (
        Array.isArray(caseOutcome.similarCases) &&
        typeof caseOutcome.similarCases[0] === 'object'
      ) {
        return caseOutcome.similarCases;
      }

      // If it's a number, create placeholder cases
      if (typeof caseOutcome.similarCases === 'number') {
        return Array.from(
          { length: Math.min(caseOutcome.similarCases, 6) },
          (_, i) => ({
            title: `Similar Case ${i + 1}`,
            description: 'Details available upon request',
          })
        );
      }

      return [];
    }, [conversationData]);

    const handleRetry = () => {
      batch(() => {
        adviceState.status.set('ready');
        adviceState.error.set(null);
        adviceActions.startRequest(true);
        adviceActions.completeRequest();
      });
    };

    return (
      <motion.div
        variants={messageVariants}
        className='flex min-h-0 w-full flex-1 flex-col overflow-hidden'
        ref={chatContainerRef}
      >
        {/* Render the CaseOutcome if available */}
        {shouldShowCaseOutcome.get() && (
          <motion.div
            initial={{ opacity: 0, y: -20, height: 0, overflow: 'hidden' }}
            animate={{ opacity: 1, y: 0, height: 'auto', overflow: 'visible' }}
            exit={{ opacity: 0, y: -20, height: 0, overflow: 'hidden' }}
            transition={{
              duration: 0.6,
              type: 'spring',
              stiffness: 100,
              damping: 15,
            }}
            className='m-1 flex-shrink-0 rounded-2xl border border-purple-200 bg-purple-50 shadow-sm dark:border-purple-800/50 dark:bg-purple-900'
          >
            <CaseOutcome
              similarCases={formattedSimilarCases}
              ratings={{
                excellent:
                  conversationData.get()?.caseOutcome?.ratings?.excellent ??
                  Math.floor(Math.random() * 100),
                good:
                  conversationData.get()?.caseOutcome?.ratings?.good ??
                  Math.floor(Math.random() * 100),
                fair:
                  conversationData.get()?.caseOutcome?.ratings?.fair ??
                  Math.floor(Math.random() * 100),
                poor:
                  conversationData.get()?.caseOutcome?.ratings?.poor ??
                  Math.floor(Math.random() * 100),
              }}
              description={conversationData.get()?.caseOutcome?.summary}
            />
            <div className='absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-purple-200 to-transparent dark:from-purple-900 sm:h-1' />
          </motion.div>
        )}

        {/* Main conversation messages */}
        <div className='scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 hover:scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 dark:hover:scrollbar-thumb-gray-600 flex min-h-0 flex-1 flex-col overflow-y-auto p-2 sm:p-4'>
          <AnimatePresence mode='wait'>
            <ConversationMessages />
            {error.get() && (
              <ErrorMessage error={error.get()} onRetry={handleRetry} />
            )}
          </AnimatePresence>
          <div ref={recentPromptRef} className='h-px w-full' />

          {/* Loading state - only show if no error */}
          {/* {isLoading.get() && !error.get() && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="mt-2"
            >
              <MessageSkeleton />
            </motion.div>
          )} */}
        </div>
      </motion.div>
    );
  }
);
